<template>
  <section class="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
    <div class="h-1.5 bg-newPrimary"></div>
    <div v-if="hasOptedIn" class="p-5 lg:p-6">
      <div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.16em] text-newPrimary">Company matches</p>
          <h2 class="mt-1 text-2xl font-bold leading-tight text-slate-950">{{ matchesHeading }}</h2>
          <p class="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Personalised recommendations based on your selected states, commodities and project stages.
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            class="inline-flex w-fit items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-newPrimary/40 hover:text-newPrimary"
            @click="openPreferences"
          >
            Edit answers
          </button>
          <button
            type="button"
            class="inline-flex w-fit items-center justify-center rounded-md px-3 py-2 text-xs font-semibold text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            @click="resetExperience"
          >
            Reset
          </button>
        </div>
      </div>

      <div v-if="isCalculating && !results" class="grid gap-3 lg:grid-cols-2">
        <div v-for="item in 10" :key="item" class="h-[96px] animate-pulse rounded-lg bg-slate-100"></div>
      </div>

      <template v-else-if="qualifiedMatches.length">
        <TransitionGroup name="company-match-list" tag="div" class="grid gap-3 lg:grid-cols-2">
          <article
            v-for="(company, index) in visibleMatches"
            :key="company.company_id"
            class="grid grid-cols-[36px_minmax(0,1fr)_76px] items-center gap-3 rounded-lg border border-slate-200 bg-white px-3.5 py-3 shadow-sm transition hover:border-newPrimary/30 hover:shadow-md"
          >
            <div
              class="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold"
              :class="rankClass(index)"
            >
              {{ index + 1 }}
            </div>
            <div class="min-w-0">
              <router-link
                v-if="company.slug"
                :to="{ name: 'ContractorCompanyPage', params: { slug: company.slug } }"
                class="line-clamp-1 text-base font-bold text-slate-950 hover:text-newPrimary"
              >
                {{ company.company_name }}
              </router-link>
              <p v-else class="line-clamp-1 text-base font-bold text-slate-950">{{ company.company_name }}</p>
              <div class="mt-1.5 flex flex-wrap gap-1.5">
                <span
                  v-for="signal in matchSignals(company)"
                  :key="`${company.company_id}-${signal}`"
                  class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
                >
                  {{ signal }}
                </span>
              </div>
              <p class="mt-1.5 line-clamp-1 text-xs leading-5 text-slate-500">{{ matchSummary(company) }}</p>
            </div>
            <div class="rounded-lg bg-emerald-50 px-2 py-2 text-center text-emerald-700 ring-1 ring-emerald-100">
              <p class="text-sm font-bold">{{ company.total_match_percent }}%</p>
              <p class="text-[11px] font-semibold">Match</p>
            </div>
          </article>
        </TransitionGroup>

        <div v-if="hasExpandableMatches" class="mt-4 flex justify-center">
          <button
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-newPrimary/40 hover:text-newPrimary"
            :aria-expanded="showAllQualifiedMatches ? 'true' : 'false'"
            @click="toggleExpandedMatches"
          >
            {{ expandMatchesLabel }}
            <ChevronDown class="h-4 w-4 transition-transform duration-300" :class="{ 'rotate-180': showAllQualifiedMatches }" />
          </button>
        </div>
      </template>

      <div v-else class="rounded-lg border border-slate-200 bg-slate-50 px-5 py-6">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p class="text-base font-bold text-slate-950">{{ emptyStateTitle }}</p>
            <p class="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{{ emptyStateCopy }}</p>
          </div>
          <button
            type="button"
            class="inline-flex shrink-0 items-center justify-center rounded-lg bg-newPrimary px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-newPrimary/90"
            @click="openPreferences"
          >
            Update answers
          </button>
        </div>
      </div>
    </div>

    <div v-else class="grid gap-6 p-5 lg:grid-cols-[0.8fr_1.55fr_0.85fr] lg:p-6">
      <div class="flex flex-col justify-center">
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 text-newPrimary ring-8 ring-sky-50/50">
          <Rocket class="h-5 w-5" />
        </div>
        <h2 class="mt-5 text-3xl font-bold leading-tight text-slate-950">Your Best Company Matches</h2>
        <p class="mt-3 max-w-sm text-sm leading-6 text-slate-600">
          We've analysed your CV to identify the companies that best match your experience, skills and career background.
        </p>
        <div v-if="results?.source?.matched_previous_employers_count" class="mt-4 inline-flex w-fit items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
          Based on {{ results.source.matched_previous_employers_count }} CV employer {{ results.source.matched_previous_employers_count === 1 ? 'match' : 'matches' }}
        </div>
      </div>

      <div class="min-w-0">
        <div class="mb-4 flex items-center justify-between gap-3">
          <p class="text-sm font-bold text-newPrimary">{{ hasOptedIn ? 'Your Top 10 Company Matches' : 'Your Top 3 Companies' }}</p>
          <button
            v-if="hasOptedIn"
            type="button"
            class="text-xs font-semibold text-slate-500 hover:text-newPrimary"
            @click="openPreferences"
          >
            Edit answers
          </button>
        </div>

        <div v-if="isCalculating && !results" class="space-y-3">
          <div v-for="item in 3" :key="item" class="h-[82px] animate-pulse rounded-lg bg-slate-100"></div>
        </div>

        <div v-else-if="results?.matches?.length" class="space-y-2.5">
          <article
            v-for="(company, index) in visibleMatches"
            :key="company.company_id"
            class="grid grid-cols-[36px_minmax(0,1fr)_76px] items-center gap-3 rounded-lg border border-slate-200 bg-white px-3.5 py-3 shadow-sm transition hover:border-newPrimary/30 hover:shadow-md"
          >
            <div
              class="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold"
              :class="rankClass(index)"
            >
              {{ index + 1 }}
            </div>
            <div class="min-w-0">
              <router-link
                v-if="company.slug"
                :to="{ name: 'ContractorCompanyPage', params: { slug: company.slug } }"
                class="line-clamp-1 text-base font-bold text-slate-950 hover:text-newPrimary"
              >
                {{ company.company_name }}
              </router-link>
              <p v-else class="line-clamp-1 text-base font-bold text-slate-950">{{ company.company_name }}</p>
              <div class="mt-1.5 flex flex-wrap gap-1.5">
                <span
                  v-for="signal in matchSignals(company)"
                  :key="`${company.company_id}-${signal}`"
                  class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
                >
                  {{ signal }}
                </span>
              </div>
              <p class="mt-1.5 line-clamp-1 text-xs leading-5 text-slate-500">{{ matchSummary(company) }}</p>
            </div>
            <div class="rounded-lg bg-emerald-50 px-2 py-2 text-center text-emerald-700 ring-1 ring-emerald-100">
              <p class="text-sm font-bold">{{ company.total_match_percent }}%</p>
              <p class="text-[11px] font-semibold">Match</p>
            </div>
          </article>
        </div>

        <div v-else class="rounded-lg border border-slate-200 bg-slate-50 px-5 py-6">
          <p class="text-base font-bold text-slate-950">{{ emptyStateTitle }}</p>
          <p class="mt-2 text-sm leading-6 text-slate-600">{{ emptyStateCopy }}</p>
        </div>
      </div>

      <div class="flex flex-col justify-center rounded-lg bg-slate-50 p-5 lg:p-6">
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 text-newPrimary">
          <Sparkles class="h-5 w-5" />
        </div>
        <p class="mt-4 text-lg font-bold leading-6 text-newPrimary">
          {{ hasOptedIn ? 'Personalised recommendations are active' : '+7 more personalised recommendations available' }}
        </p>
        <p class="mt-3 text-sm leading-6 text-slate-600">
          {{ hasOptedIn ? 'These matches use your selected states, commodities and project stages.' : 'These recommendations are based on your CV alone. Complete your profile to unlock more personalised matches based on your experience, preferred locations, commodities and project background.' }}
        </p>
        <button
          type="button"
          class="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-newPrimary px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-newPrimary/90"
          @click="openPreferences"
        >
          {{ primaryActionLabel }}
          <ChevronRight class="h-4 w-4" />
        </button>
        <button
          v-if="hasStoredPreferences"
          type="button"
          class="mt-2 text-center text-xs font-semibold text-slate-400 transition hover:text-slate-700"
          @click="resetExperience"
        >
          Reset test experience
        </button>
        <p v-if="!hasOptedIn" class="mt-2 text-center text-xs font-medium text-slate-500">Takes less than 2 minutes</p>
      </div>
    </div>

    <div v-if="error" class="mx-5 mb-5 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 lg:mx-6">
      {{ error }}
    </div>

    <div v-if="showPreferencesModal" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4 py-6">
      <section class="max-h-full w-full max-w-3xl overflow-auto rounded-lg bg-white shadow-2xl">
        <div class="border-b border-slate-200 px-6 py-5">
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-newPrimary">Match preferences</p>
          <h2 class="mt-2 text-2xl font-bold text-slate-950">Tune your company matches</h2>
          <p class="mt-2 text-sm leading-6 text-slate-600">Answer once, then return here whenever you want refreshed company recommendations.</p>
        </div>

        <div class="space-y-6 px-6 py-5">
          <div>
            <p class="text-sm font-semibold text-slate-800">Which states would you work in?</p>
            <div class="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <label v-for="state in stateOptions" :key="state.value" class="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition" :class="form.states.includes(state.value) ? 'border-newPrimary bg-sky-50 text-slate-950' : 'border-slate-200 text-slate-600 hover:bg-slate-50'">
                <input v-model="form.states" type="checkbox" class="h-4 w-4 rounded border-slate-300 text-newPrimary focus:ring-newPrimary/30" :value="state.value" />
                <span>{{ state.label }}</span>
              </label>
            </div>
          </div>

          <div>
            <p class="text-sm font-semibold text-slate-800">Which commodities have you worked on?</p>
            <input v-model="commoditySearch" type="text" placeholder="Search commodities..." class="mt-3 h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-newPrimary focus:ring-2 focus:ring-newPrimary/20" />
            <div v-if="form.commodities.length" class="mt-3 flex flex-wrap gap-2">
              <button v-for="commodity in form.commodities" :key="commodity" type="button" class="rounded-full bg-newPrimary px-3 py-1 text-xs font-semibold text-white" @click="toggleListItem('commodities', commodity)">
                {{ commodity }}
              </button>
            </div>
            <div class="mt-3 flex max-h-40 flex-wrap gap-2 overflow-auto">
              <button v-for="commodity in filteredCommodities" :key="commodity" type="button" class="rounded-full border px-3 py-1 text-xs font-semibold transition" :class="form.commodities.includes(commodity) ? 'border-newPrimary bg-sky-50 text-newPrimary' : 'border-slate-300 text-slate-600 hover:border-newPrimary hover:text-slate-900'" @click="toggleListItem('commodities', commodity)">
                {{ commodity }}
              </button>
            </div>
          </div>

          <div>
            <p class="text-sm font-semibold text-slate-800">Which project stages have you worked on?</p>
            <div class="mt-3 grid gap-2 sm:grid-cols-2">
              <label v-for="stage in stageOptions" :key="stage.value" class="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition" :class="form.projectStages.includes(stage.value) ? 'border-newPrimary bg-sky-50 text-slate-950' : 'border-slate-200 text-slate-600 hover:bg-slate-50'">
                <input v-model="form.projectStages" type="checkbox" class="h-4 w-4 rounded border-slate-300 text-newPrimary focus:ring-newPrimary/30" :value="stage.value" />
                <span>{{ stage.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="flex flex-col-reverse gap-2 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end">
          <button type="button" class="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50" @click="closePreferences">Cancel</button>
          <button type="button" class="inline-flex items-center justify-center rounded-lg bg-newPrimary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-newPrimary/90 disabled:cursor-not-allowed disabled:opacity-60" :disabled="isCalculating" @click="calculateMatches">
            <span v-if="isCalculating" class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></span>
            Show my matches
          </button>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { COMMODITIES } from '@/constants/lookups'
import { getContractorCompanyMatches } from '@/api/contractor/company'
import { ChevronDown, ChevronRight, Rocket, Sparkles } from 'lucide-vue-next'

export default {
  name: 'CompanyMatchesPanel',
  components: {
    ChevronDown,
    ChevronRight,
    Rocket,
    Sparkles,
  },
  props: {
    profile: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isCalculating: false,
      showPreferencesModal: false,
      hasOptedIn: false,
      hasStoredPreferences: false,
      showAllQualifiedMatches: false,
      error: '',
      commoditySearch: '',
      results: null,
      form: {
        states: [],
        commodities: [],
        projectStages: [],
      },
      stateOptions: [
        { value: 'NSW', label: 'NSW' },
        { value: 'QLD', label: 'QLD' },
        { value: 'VIC', label: 'VIC' },
        { value: 'WA', label: 'WA' },
        { value: 'SA', label: 'SA' },
        { value: 'TAS', label: 'TAS' },
        { value: 'NT', label: 'NT' },
        { value: 'ACT', label: 'ACT' },
      ],
      stageOptions: [
        { label: 'Greenfields', value: 'Exploration' },
        { label: 'Brownfields', value: 'Brownfield' },
        { label: 'Resource Definition', value: 'Pre-Feasibility' },
        { label: 'Feasibility', value: 'Feasibility' },
        { label: 'Development', value: 'Development' },
        { label: 'Production', value: 'Production' },
      ],
    }
  },
  computed: {
    storageKey() {
      return `contractorCompanyMatches:${this.profile.contractor_id || this.profile.uuid || 'unknown'}`
    },
    filteredCommodities() {
      const query = this.commoditySearch.trim().toLowerCase()
      const options = query
        ? COMMODITIES.filter((commodity) => commodity.toLowerCase().includes(query))
        : COMMODITIES
      return options.slice(0, 32)
    },
    selectedStageLabels() {
      return this.form.projectStages.map((value) => this.stageOptions.find((stage) => stage.value === value)?.label || value)
    },
    hiddenPreferenceCount() {
      return Math.max(0, this.form.commodities.length - 4) + Math.max(0, this.selectedStageLabels.length - 3)
    },
    defaultMatchLimit() {
      return this.hasOptedIn ? 10 : 3
    },
    qualifiedMatches() {
      const matches = this.results?.matches || []
      if (!this.hasOptedIn) return matches

      return matches.filter((company) => Number(company.total_match_percent || 0) >= 60)
    },
    additionalQualifiedCount() {
      return Math.max(0, this.qualifiedMatches.length - this.defaultMatchLimit)
    },
    hasExpandableMatches() {
      return this.hasOptedIn && this.additionalQualifiedCount > 0
    },
    matchesHeading() {
      return this.showAllQualifiedMatches && this.hasExpandableMatches
        ? 'Your 60%+ Company Matches'
        : 'Your Top 10 Company Matches'
    },
    expandMatchesLabel() {
      if (!this.showAllQualifiedMatches) {
        return `Show ${this.additionalQualifiedCount} more ${this.additionalQualifiedCount === 1 ? 'match' : 'matches'} scoring 60%+`
      }

      return 'Show top 10 only'
    },
    visibleMatches() {
      if (this.showAllQualifiedMatches && this.hasExpandableMatches) {
        return this.qualifiedMatches
      }

      return this.qualifiedMatches.slice(0, this.defaultMatchLimit)
    },
    hasNoCvSourceEmployers() {
      return !this.hasOptedIn
        && !!this.results
        && Number(this.results?.source?.matched_previous_employers_count || 0) <= 0
    },
    primaryActionLabel() {
      if (this.hasOptedIn) {
        return 'Update My Matches'
      }

      return this.hasNoCvSourceEmployers ? 'Complete My Profile' : 'Find More Matches'
    },
    emptyStateTitle() {
      if (this.hasOptedIn) {
        return 'No companies matched those answers yet'
      }

      const employerMatches = Number(this.results?.source?.matched_previous_employers_count || 0)
      return employerMatches > 0
        ? 'No similar companies found from your CV yet'
        : 'We need a little more information to find your best company matches'
    },
    emptyStateCopy() {
      if (this.hasOptedIn) {
        return 'Try broadening the states, commodities or project stages selected. We will use the updated answers to look across more mining companies.'
      }

      const employerMatches = Number(this.results?.source?.matched_previous_employers_count || 0)
      if (employerMatches > 0) {
        return 'We found companies in your work history, but not enough overlapping project data to recommend similar companies automatically. Add your preferences to unlock personalised matches.'
      }

      return 'We couldn\'t identify any strong matches from your CV alone. Answer a few quick questions about your experience and preferences, and your AI Recruiter will find the companies that best fit your profile.'
    },
  },
  mounted() {
    this.restorePreferences()
    if (this.hasOptedIn && this.isFormComplete()) {
      this.calculateMatches({ silent: true })
    } else {
      this.calculateCvMatches()
    }
  },
  methods: {
    restorePreferences() {
      const raw = localStorage.getItem(this.storageKey)
      this.hasStoredPreferences = !!raw
      if (!raw) {
        this.form = this.defaultForm()
        return
      }

      try {
        const saved = JSON.parse(raw)
        this.hasOptedIn = !!saved.hasOptedIn
        this.form = {
          states: Array.isArray(saved.states) ? saved.states : [],
          commodities: Array.isArray(saved.commodities) ? saved.commodities : [],
          projectStages: Array.isArray(saved.projectStages) ? saved.projectStages : [],
        }
      } catch {
        localStorage.removeItem(this.storageKey)
      }
    },
    defaultForm() {
      return {
        states: this.profile.state ? [String(this.profile.state).toUpperCase()] : [],
        commodities: [],
        projectStages: [],
      }
    },
    savePreferences() {
      localStorage.setItem(this.storageKey, JSON.stringify({ hasOptedIn: this.hasOptedIn, ...this.form }))
      this.hasStoredPreferences = true
    },
    resetExperience() {
      localStorage.removeItem(this.storageKey)
      this.hasOptedIn = false
      this.hasStoredPreferences = false
      this.showPreferencesModal = false
      this.error = ''
      this.commoditySearch = ''
      this.results = null
      this.showAllQualifiedMatches = false
      this.form = this.defaultForm()
      this.calculateCvMatches()
    },
    primaryAction() {
      if (!this.hasOptedIn || !this.isFormComplete()) {
        this.hasOptedIn = true
        this.showPreferencesModal = true
        this.savePreferences()
        return
      }
      this.calculateMatches()
    },
    openPreferences() {
      this.error = ''
      this.showPreferencesModal = true
    },
    closePreferences() {
      this.showPreferencesModal = false
      this.savePreferences()
    },
    toggleListItem(key, value) {
      const list = this.form[key]
      const index = list.indexOf(value)
      if (index >= 0) list.splice(index, 1)
      else list.push(value)
      this.savePreferences()
    },
    isFormComplete() {
      return !!this.form.states.length && !!this.form.commodities.length && !!this.form.projectStages.length
    },
    validateForm() {
      if (!this.form.states.length) return 'Select at least one state.'
      if (!this.form.commodities.length) return 'Select at least one commodity.'
      if (!this.form.projectStages.length) return 'Select at least one project stage.'
      if (!this.profile.contractor_id) return 'Missing contractor id. Please refresh your profile data.'
      return ''
    },
    async calculateCvMatches() {
      if (!this.profile.contractor_id) {
        this.error = 'Missing contractor id. Please refresh your profile data.'
        return
      }

      this.isCalculating = true
      this.error = ''
      try {
        const { data } = await getContractorCompanyMatches(Number(this.profile.contractor_id), {
          mode: 'cv',
          limit: 3,
        })
        this.results = data
        this.showAllQualifiedMatches = false
      } catch (error) {
        this.error = error?.response?.data?.error || 'Could not load company matches from your CV.'
      } finally {
        this.isCalculating = false
      }
    },
    async calculateMatches(options = {}) {
      this.error = this.validateForm()
      if (this.error) {
        this.showPreferencesModal = true
        return
      }

      this.isCalculating = true
      this.hasOptedIn = true
      this.savePreferences()
      try {
        const { data } = await getContractorCompanyMatches(Number(this.profile.contractor_id), {
          commodities: this.form.commodities,
          project_stages: this.form.projectStages,
          states: this.form.states,
          limit: 100,
          minimum_match_percent: 60,
          return_all_qualified: true,
        })
        this.results = data
        this.showAllQualifiedMatches = false
        this.showPreferencesModal = false
      } catch (error) {
        this.error = error?.response?.data?.error || 'Could not calculate company matches.'
        if (!options.silent) this.showPreferencesModal = true
      } finally {
        this.isCalculating = false
      }
    },
    toggleExpandedMatches() {
      this.showAllQualifiedMatches = !this.showAllQualifiedMatches
    },
    rankClass(index) {
      if (index === 0) return 'bg-amber-100 text-amber-700'
      if (index === 1) return 'bg-slate-200 text-slate-600'
      if (index === 2) return 'bg-orange-100 text-orange-700'
      return 'bg-slate-100 text-slate-500'
    },
    matchSignals(company) {
      const signals = [
        ...(company.matched_commodities || []).slice(0, 2),
        ...(company.matched_project_stages || []).slice(0, 1),
        ...(company.matched_company_size_bands || []).slice(0, 1),
        ...(company.matched_states || []).slice(0, 1).map((state) => `${state} projects`),
      ]

      if (signals.length) return signals
      if (company.industry_subcategory) return [company.industry_subcategory]
      return ['Mining']
    },
    matchSummary(company) {
      const commodities = company.matched_commodities || []
      const stages = company.matched_project_stages || []
      const sizeBands = company.matched_company_size_bands || []
      const states = company.matched_states || []

      if (commodities.length && stages.length && sizeBands.length && states.length) {
        return `Strong fit across commodity, stage, company size and location.`
      }
      if (commodities.length && stages.length && states.length) {
        return `Similar commodity, project stage and location signals.`
      }
      if (commodities.length && stages.length && sizeBands.length) {
        return `Similar commodity, stage and company-size exposure.`
      }
      if (commodities.length && stages.length) {
        return `Similar commodity and project stage exposure.`
      }
      if (sizeBands.length && states.length) {
        return `Similar company size with projects in ${states.slice(0, 2).join(', ')}.`
      }
      if (commodities.length && states.length) {
        return `Similar commodity exposure with projects in ${states.slice(0, 2).join(', ')}.`
      }
      if (stages.length && states.length) {
        return `Similar project stage exposure in ${states.slice(0, 2).join(', ')}.`
      }
      if (commodities.length) {
        return `Similar commodity exposure from your CV history.`
      }
      if (stages.length) {
        return `Similar project stage profile from your CV history.`
      }
      if (sizeBands.length) {
        return `Similar company-size profile to your CV history.`
      }
      if (states.length) {
        return `Projects in ${states.slice(0, 2).join(', ')} match your location signal.`
      }

      return company.industry_subcategory
        ? `Relevant ${company.industry_subcategory.toLowerCase()} profile in mining.`
        : 'Relevant mining company profile.'
    },
  },
}
</script>

<style scoped>
.company-match-list-move,
.company-match-list-enter-active,
.company-match-list-leave-active {
  transition:
    opacity 220ms ease,
    transform 220ms ease;
}

.company-match-list-enter-from,
.company-match-list-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.company-match-list-leave-active {
  position: absolute;
}
</style>
